import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type RowData,
  type SortingState,
} from "@tanstack/react-table";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { distinctValues, sortRows, type SortDirection } from "../data/cockpit-data";
import { badgeVariant, ratioBandLabel, ratioBandTone } from "../format";
import { TableControls, type TableSelectControl } from "./TableControls";

export type StatsColumn = {
  key: string;
  label: string;
  strong?: boolean;
  /** Right-align + tabular numerals. */
  numeric?: boolean;
  /** Presentation variant: 'mono' for IDs/ASINs, 'band-ratio' renders a profit-yield band. */
  variant?: "mono" | "band-ratio";
  /** Numeric display format: 'thousands' adds group separators; 'decimal2' fixes to 2 dp. */
  format?: "thousands" | "decimal2";
};

/**
 * The shell's `DataTable` augments `ColumnMeta` with its own closed set of
 * presentation fields (align/width/sortable), so handing TanStack a bare
 * `StatsColumn` as `meta` no longer typechecks — the two shapes share no
 * properties. Carry it under a named key of our own instead: interface
 * augmentation merges, so this adds one optional field without touching the
 * shell-owned type.
 */
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    stats?: StatsColumn;
  }
}

export type StatsFilter = { key: string; label: string };

/**
 * The Stats tab's two tables, filtered and sorted in the browser.
 *
 * `/state/stats` returns the whole read model in one response, so the sorting
 * and filtering happen over rows already in hand rather than as another round
 * trip. Domain ordering stays in the data layer (`sortRows`, `distinctValues`)
 * and TanStack owns only the header state — the split that keeps a sort rule
 * from being written twice.
 */
export function SortableStatsTable({
  title,
  rows,
  columns,
  filters,
  emptyText = "No data yet for this range.",
}: {
  title: string;
  rows: Record<string, unknown>[];
  columns: StatsColumn[];
  filters: StatsFilter[];
  emptyText?: string;
}) {
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [textFilter, setTextFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: columns[0]?.key ?? "", desc: false }]);

  const selects: TableSelectControl[] = useMemo(
    () =>
      filters.map((filter) => ({
        id: filter.key,
        label: filter.label,
        value: filterValues[filter.key] ?? "",
        options: distinctValues(rows, filter.key).map((value) => ({ label: value, value })),
      })),
    [filterValues, filters, rows],
  );

  const visible = useMemo(() => {
    const text = textFilter.trim().toLowerCase();
    const filtered = rows.filter((row) => {
      for (const filter of filters) {
        const selected = filterValues[filter.key];
        if (selected && !matchesFilter(row[filter.key], selected)) return false;
      }
      if (text) {
        const haystack = columns
          .map((column) => renderText(row, column))
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(text)) return false;
      }
      return true;
    });
    const sort = sorting[0];
    if (!sort?.id) return filtered;
    const dir: SortDirection = sort.desc ? "desc" : "asc";
    return sortRows(filtered, sort.id, dir);
  }, [columns, filterValues, filters, rows, sorting, textFilter]);

  const columnDefs = useMemo<ColumnDef<Record<string, unknown>>[]>(
    () =>
      columns.map((column) => ({
        id: column.key,
        accessorFn: (row: Record<string, unknown>) => renderText(row, column),
        header: column.label,
        cell: ({ row }: { row: { original: Record<string, unknown> } }) => renderCell(row.original, column),
        meta: { stats: column },
      })),
    [columns],
  );

  const table = useReactTable({
    data: visible,
    columns: columnDefs,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row, index) => rowKey(row, index),
    manualSorting: true,
    manualFiltering: true,
    enableSortingRemoval: false,
    sortDescFirst: false,
  });

  function updateSelect(id: string, value: string) {
    setFilterValues((current) => ({ ...current, [id]: value }));
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <h3 className="text-base font-semibold text-ks-ink">{title}</h3>
        <TableControls
          selects={selects}
          textValue={textFilter}
          textPlaceholder="Filter table"
          onSelectChange={updateSelect}
          onTextChange={setTextFilter}
          resultCount={visible.length}
          totalCount={rows.length}
          countNoun="rows"
        />
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const column = header.column.columnDef.meta?.stats;
                    const sorted = header.column.getIsSorted();
                    return (
                      <TableHead
                        key={header.id}
                        aria-sort={
                          sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : undefined
                        }
                        className={column?.numeric ? "text-right" : undefined}
                      >
                        <Button
                          variant="ghost"
                          size="xs"
                          className={`-mx-2 uppercase tracking-wide ${column?.numeric ? "ml-auto" : ""}`}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sorted === "asc" ? (
                            <ChevronUp aria-hidden="true" />
                          ) : sorted === "desc" ? (
                            <ChevronDown aria-hidden="true" />
                          ) : null}
                        </Button>
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {visible.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="py-8 text-center text-muted-foreground">
                    {emptyText}
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      const column = cell.column.columnDef.meta?.stats;
                      return (
                        <TableCell
                          key={cell.id}
                          className={`${column?.numeric ? "text-right tabular-nums" : ""} ${
                            column?.strong ? "font-medium text-ks-ink" : "text-muted-foreground"
                          }`}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function renderCell(row: Record<string, unknown>, column: StatsColumn) {
  const text = renderText(row, column);
  if (column.variant === "band-ratio") {
    const value = Number.parseFloat(text);
    if (!Number.isNaN(value)) {
      // The badge weights the figure and the title names the band. Meteoric
      // painted the number in one of four shades, which told a reader something
      // was true of it without ever telling them what.
      return (
        <Badge variant={badgeVariant(ratioBandTone(value))} title={ratioBandLabel(value)} className="tabular-nums">
          {text}
        </Badge>
      );
    }
  }
  if (column.variant === "mono") {
    return <span className="font-mono text-xs">{text}</span>;
  }
  return text;
}

function renderText(row: Record<string, unknown>, column: StatsColumn): string {
  const value = row[column.key];
  if (value === null || value === undefined || value === "") return "n/a";
  if (Array.isArray(value)) return value.join(", ");
  if (column.format) {
    const formatted = formatNumeric(value, column.format);
    if (formatted !== null) return formatted;
  }
  return String(value);
}

// Returns null for non-numeric values so the caller falls back to the raw string
// — never masks bad data as a formatted zero.
function formatNumeric(value: unknown, format: NonNullable<StatsColumn["format"]>): string | null {
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return null;
  if (format === "decimal2") return num.toFixed(2);
  return num.toLocaleString("en-US");
}

function matchesFilter(value: unknown, selected: string): boolean {
  if (Array.isArray(value)) return value.some((item) => String(item) === selected);
  return String(value ?? "") === selected;
}

function rowKey(row: Record<string, unknown>, index: number): string {
  return String(row.target_group ?? row.domino ?? row.asin ?? index);
}
